import React, { useState, useEffect } from "react";
import styles from "./CommunityEngagements.module.css";
import Image from "next/image";
interface Card {
  id: number;
  link: {
    url: string;
  };
  image: {
    url: string;
    alternativeText?: string;
  };
  mobile_image?: {
    url: string;
    alternativeText?: string;
  };
}

interface SpeakerHighlightsProps {
  cards: Card[];
  Speaker_highlights_title: string;
}

interface Props {
  data: SpeakerHighlightsProps;
}

const SpeakerHighlights: React.FC<Props> = ({ data }) => {
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    // Check if we're on the client side
    if (typeof window !== "undefined") {
      // Set initial state
      setIsMobile(window.innerWidth <= 768);

      // Add event listener for window resize
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      window.addEventListener("resize", handleResize);

      // Clean up event listener
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  const handlePlay = (id: number) => {
    setPlayingVideoId(id);
  };

  const { cards, Speaker_highlights_title } = data;

  return (
    <div className="container">
      <h2 className={`${styles.CEheader} mt-3`}>{Speaker_highlights_title}</h2>
      <div className={styles.videoCardContainer}>
        {cards.map((card) => (
          <VideoCard
            key={card?.id}
            videoUrl={card?.link.url}
            image={card?.image}
            mobileImage={card?.mobile_image}
            isMobile={isMobile}
            isPlaying={playingVideoId === card?.id}
            onPlay={() => handlePlay(card?.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default SpeakerHighlights;

interface VideoCardProps {
  videoUrl: string;
  image: {
    url: string;
    alternativeText?: string;
  };
  mobileImage?: {
    url: string;
    alternativeText?: string;
  };
  isMobile: boolean;
  isPlaying: boolean;
  onPlay: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({
  videoUrl,
  image,
  mobileImage,
  isMobile,
  isPlaying,
  onPlay,
}) => {
  let videoId: string | null = null;
  let thumbnailUrl: string | null = null;

  try {
    const url = new URL(
      videoUrl.startsWith("http") ? videoUrl : `https://${videoUrl}`
    );
    videoId = url?.searchParams?.get("v");

    // Generate YouTube thumbnail URL if we have a video ID
    if (videoId) {
      // Using the high quality thumbnail (hqdefault.jpg)
      thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  } catch (err) {
    console.error("Invalid video URL:", videoUrl, err);
  }

  // Create a fallback image object using the YouTube thumbnail
  const thumbnailImage = thumbnailUrl
    ? {
        url: thumbnailUrl,
        alternativeText: "Video thumbnail",
      }
    : null;

  // Determine which image to use with fallback to YouTube thumbnail
  let displayImage;
  if (isMobile && mobileImage?.url) {
    // Use mobile image if on mobile and mobile image exists
    displayImage = mobileImage;
  } else if (image?.url) {
    // Use desktop image if it exists
    displayImage = image;
  } else if (thumbnailImage) {
    // Use YouTube thumbnail as fallback
    displayImage = thumbnailImage;
  } else {
    // No image available
    displayImage = null;
  }

  return (
    <div className={styles.videoCard}>
      {!isPlaying || !videoId ? (
        <div className={styles.cover} onClick={onPlay}>
          <div className={styles.overlay}></div>
          {displayImage?.url ? (
            <Image
              src={displayImage.url}
              alt={displayImage.alternativeText || "Video thumbnail"}
              title={displayImage.alternativeText || "Video thumbnail"}
              fill
              style={{
                objectFit: "cover",
              }}
              className={styles.thumbnail}
            />
          ) : (
            // If no image is available, show a placeholder with play button
            <div className={styles.noThumbnail}>
              <span>Video</span>
            </div>
          )}
          <button aria-label="Play Button" className={styles.playButton}>
            ▶
          </button>
        </div>
      ) : (
        <div className={styles.videoBanner}>
          <iframe
            className={styles.youtubeVideo}
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&modestbranding=1&controls=1&rel=0&showinfo=0&iv_load_policy=3&fs=1&disablekb=1`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin"
          ></iframe>
        </div>
      )}
    </div>
  );
};
